import { Router } from '@angular/router';
import { Directive, EventEmitter, Injectable, Output } from '@angular/core';
import { TriggerbeeApi, TriggerbeeRequester } from './../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../core/logger.service';
import { People, PeopleResponse } from './people';
import { QueryContext } from '../search/query-context';
import { Observable, Subject } from 'rxjs';
import { CustomProfileProperty } from 'app/back-office/account.model';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { DeleteValidatedModel, ImportValidatedModel } from './import-validated.model';

@Directive()
@Injectable()
export class PeopleService {
  private readonly logger: Logger;
  private readonly apiBaseUrl: string;
  private pageCount;
  @Output() public refreshPeopleData = new EventEmitter<string>();
  public peopleQueryBS = new Subject<QueryContext>();

  constructor(
    loggerFactory: LoggerFactory,
    private readonly requester: TriggerbeeRequester,
    protected readonly router: Router,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('PeopleService');
    this.apiBaseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
  }

  private getContactsResource(subResource?: string): string {
    return 'people' + (subResource || '');
  }

  public peopleGetQuery(query: any): void {
    this.peopleQueryBS.next(query);
  }

  public async deleteContactAsync(person: People): Promise<boolean> {
    // Execute query
    const response = await this.requester.deleteAsync<void>(TriggerbeeApi.Gateway, `profiles/${person.id}`);
    if (!response.isPositive()) {
      this.logger.error(`Could not delete profile: ${response.toString()}`);
      return null;
    }
    return true;
  }

  public async exportPerson(contactId: string): Promise<string> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/profiles/' + contactId + '/export';
    return await this.http.getDownload(url).toPromise();
  }

  // People Migration Start - few methods above will be obsolete

  public async getPeopleByQueryContext(queryContext: QueryContext, resource = '/search'): Promise<PeopleResponse> {
    if (queryContext && queryContext.segment && queryContext.segment.criterias) {
      if (queryContext.segment.criterias['goals']) {
        queryContext.segment.criterias['Goals'] = queryContext.segment.criterias['goals'];
        queryContext.segment.criterias['goals'] = undefined;
      }
    }

    // Execute query
    const response = await this.requester.postAsync<PeopleResponse>(
      TriggerbeeApi.Gateway,
      `profiles${resource}`,
      this.addDefaultOrderBy(queryContext),
    );
    if (!response.isPositive()) {
      this.logger.error(`Could not get Contacts : ${response.toString()}`);
    }

    return response.data;
  }

  public async countPeopleByQueryContext(queryContext: QueryContext, total: number): Promise<number> {
    // Count
    if (queryContext.take === total) {
      const count = await this.requester.postAsync<number>(TriggerbeeApi.Gateway, 'profiles/count', queryContext);
      return count.data;
    }

    return total;
  }

  public deleteProfilesByDate(date: Date): Observable<null> {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return this.http.post(`${this.apiBaseUrl}/profiles/delete-by-date?deleteOlderThan=${year}-${month}-${day}`, null);
  }

  public get getPageCount() {
    return this.pageCount;
  }

  private addDefaultOrderBy(queryContext: QueryContext): QueryContext {
    if (!queryContext.orderBy) {
      queryContext.orderBy = '-LastActivity'; // Order by LastActivity DESC in no other order is set
    }
    return queryContext;
  }

  public validateImportFile(file: FormData): Observable<ImportValidatedModel> {
    return this.http.upload(`${this.apiBaseUrl}/profiles/import/validate`, file);
  }

  public uploadImportFile(file: FormData, clearData: boolean): Observable<any> {
    return this.http.upload(`${this.apiBaseUrl}/profiles/import`, file, {
      clearDataWhenEmptyColumns: clearData,
    });
  }

  public validateDeleteFile(file: FormData): Observable<DeleteValidatedModel> {
    return this.http.upload(`${this.apiBaseUrl}/profiles/delete-by-file/validate`, file);
  }

  public deleteProfilesWithFile(file: FormData, deleteInAccounts: boolean): Observable<any> {
    return this.http.upload(`${this.apiBaseUrl}/profiles/delete-by-file`, file, {
      deleteInGroupedAccounts: deleteInAccounts,
    });
  }

  public validateDeleteIdentifiers(identifiers: string): Observable<DeleteValidatedModel> {
    return this.http.post(`${this.apiBaseUrl}/profiles/delete-by-identifiers/validate`, {
      identifiers: identifiers,
    });
  }

  public deleteWithIdentifiers(identifiers: string, deleteInAccounts: boolean): Observable<any> {
    return this.http.post(`${this.apiBaseUrl}/profiles/delete-by-identifiers`, {
      identifiers: identifiers,
      deleteInGroupedAccounts: deleteInAccounts,
    });
  }

  public getProfileFields(): Observable<CustomProfileProperty[]> {
    return this.http.get(`${this.apiBaseUrl}/custom-profile-properties`);
  }

  public updateProfileFields(profileField: CustomProfileProperty): Observable<CustomProfileProperty> {
    return this.http.put(`${this.apiBaseUrl}/custom-profile-properties/${profileField.id}`, {
      name: profileField.name,
    });
  }

  public createProfileFields(profileField: CustomProfileProperty): Observable<CustomProfileProperty> {
    return this.http.post(`${this.apiBaseUrl}/custom-profile-properties`, {
      ...profileField,
      mapFrom: profileField.mapTo,
    });
  }

  public deleteProfileFields(id: number): Observable<null> {
    return this.http.delete(`${this.apiBaseUrl}/custom-profile-properties/${id}`);
  }
}
